import FileExcelOutlined from '@ant-design/icons/FileExcelOutlined'
import { Button, Tooltip, message } from '@surecompanies/core_components'
import dayjs from 'dayjs'
import { downloadFile } from 'helpers/download-file'
import React, { useMemo, useState } from 'react'
import { downloadPaymentBotAttempts } from '../../../../api/payment-bot-dashboard-api-client'

const XLSX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

interface Props {
  month: number
  year: number
  carrierName?: string
}

function toCarrierSlug(carrierName?: string): string {
  if (!carrierName) return 'all-carriers'
  return carrierName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Read an HTTP status off a rejected request without asserting the error's shape.
 *
 * `catch (error: any)` would let an Axios-shape change silently route a real 403
 * to the generic toast; narrowing through `Reflect.get` keeps the checking on
 * while still tolerating a non-Axios rejection.
 */
function responseStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined
  const response = Reflect.get(error, 'response')
  if (typeof response !== 'object' || response === null) return undefined
  const status = Reflect.get(response, 'status')
  return typeof status === 'number' ? status : undefined
}

export function DownloadAttemptsButton({ month, year, carrierName }: Props) {
  const [loading, setLoading] = useState(false)
  const periodLabel = dayjs().year(year).month(month).format('YYYY-MM')
  const scopeLabel = carrierName ?? 'all carriers'
  const tooltip = useMemo(
    () =>
      `All bot payment attempts — ${scopeLabel}, ${dayjs()
        .year(year)
        .month(month)
        .format(
          'MMMM YYYY'
        )}. Ignores the table filter; one row per attempt, shaded per benefit.`,
    [month, scopeLabel, year]
  )

  const handleDownload = async () => {
    setLoading(true)
    try {
      const { blob, filename } = await downloadPaymentBotAttempts({
        month,
        year,
        ...(carrierName ? { carrierName } : {}),
      })
      // The server names the attachment; the locally-built name is only a
      // fallback for a response with no readable Content-Disposition.
      downloadFile(
        blob,
        filename ??
          `bot-attempts_${periodLabel}_${toCarrierSlug(carrierName)}.xlsx`,
        XLSX_MIME_TYPE
      )
      message.success('Report downloaded successfully')
    } catch (error: unknown) {
      console.error('Payment bot attempt export failed:', error)
      if (responseStatus(error) === 403) {
        message.error('Only platform admins can export payment bot attempts.')
      } else {
        message.error('Failed to download payment bot attempts.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Tooltip placement="topLeft" title={tooltip}>
      <Button
        variant="outlined"
        icon={<FileExcelOutlined />}
        onClick={handleDownload}
        loading={loading}
        disabled={loading}
        aria-label="Download payment bot attempts"
        data-testid="download-payment-bot-attempts"
      >
        Download Excel
      </Button>
    </Tooltip>
  )
}
